<template>
  <div class="md:hidden">
    <MobileSearchBar />
  </div>
  <div class="min-h-screen bg-gray-50">
    <!-- ✅ Contact Header Image -->
    <div v-if="contactData.image" class="w-full mb-6">
      <img
        :src="getFullImageUrl(contactData.image)"
        alt="Contact Header"
        class="w-full max-h-[80vh] aspect-[3/1] sm:aspect-[3/1] md:aspect-[3/1] lg:aspect-[4/1] md:object-cover sm:object-none object-center rounded-b-lg shadow"
        @error="handleImageError"
      />
    </div>

    <!-- Main Content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <!-- Page Header -->
      <div class="text-center mb-12">
        <h1 class="text-4xl font-extrabold text-gray-900 mb-4">
          {{ $t("contact.title") }}
        </h1>
      </div>

      <!-- Contact Content -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-12">
        <!-- Contact Info -->
        <div class="bg-white p-8 rounded-lg shadow-md">
          <h2 class="text-2xl font-bold text-gray-800 mb-6">
            {{ $t("contact.contactInfo") }}
          </h2>

          <div class="space-y-6">
            <!-- Address -->
            <!-- Address Section -->
            <div class="flex items-start">
              <div
                class="flex-shrink-0 bg-teal-100 p-3 rounded-full text-teal-600"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <div class="ml-4">
                <h3 class="text-lg font-medium text-gray-800">
                  {{ $t("contact.address") }}
                </h3>
                <p class="mt-1 text-gray-600">
                  <a
                    :href="contactData.map_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="hover:text-teal-600 transition underline"
                  >
                    {{ contactData.translations[currentLang]?.address }}
                  </a>
                </p>
              </div>
            </div>

            <!-- Phone -->
            <div class="flex items-start">
              <div
                class="flex-shrink-0 bg-teal-100 p-3 rounded-full text-teal-600"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
              </div>
              <div class="ml-4">
                <h3 class="text-lg font-medium text-gray-800">
                  {{ $t("contact.phone") }}
                </h3>
                <p class="mt-1 text-gray-600">
                  <a
                    :href="`tel:${contactData.translations[currentLang].phone}`"
                    class="hover:text-teal-600 transition"
                  >
                    {{ contactData.translations[currentLang].phone }}
                  </a>
                </p>
              </div>
            </div>

            <!-- Email -->
            <div class="flex items-start">
              <div
                class="flex-shrink-0 bg-teal-100 p-3 rounded-full text-teal-600"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div class="ml-4">
                <h3 class="text-lg font-medium text-gray-800">
                  {{ $t("contact.email") }}
                </h3>
                <p class="mt-1 text-gray-600">
                  <a
                    :href="`mailto:${contactData.translations[currentLang].email}`"
                    class="hover:text-teal-600 transition"
                  >
                    {{ contactData.translations[currentLang].email }}
                  </a>
                </p>
              </div>
            </div>

            <!-- Working Hours -->
            <div class="flex items-start">
              <div
                class="flex-shrink-0 bg-teal-100 p-3 rounded-full text-teal-600"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div class="ml-4">
                <h3 class="text-lg font-medium text-gray-800">
                  {{ $t("contact.workingHours") }}
                </h3>
                <p class="mt-1 text-gray-600">
                  {{ contactData.working_hours }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- About Us & Map -->
        <div class="space-y-6">
          <!-- About Us -->
          <div class="bg-white p-8 rounded-lg shadow-md">
            <h2 class="text-2xl font-bold text-gray-800 mb-4">
              {{ $t("contact.aboutUs") }}
            </h2>
            <div
              class="prose text-gray-600 max-w-none"
              v-html="formattedAboutText"
            ></div>
          </div>

          <!-- Map -->
          <!-- <div class="bg-white p-4 rounded-lg shadow-md">
            <iframe 
              :src="contactData.map_url" 
              width="100%" 
              height="400" 
              style="border:0;" 
              allowfullscreen="" 
              loading="lazy"
              class="rounded-lg"
              referrerpolicy="no-referrer-when-downgrade">
            </iframe>
          </div> -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import MobileSearchBar from "@/components/MobileSearchBar.vue";
export default {
  name: "ContactPage",
  data() {
    return {
      contactData: {
        translations: {
          en: {},
          ar: {},
        },
        working_hours: "",
        map_url: "",
      },
    };
  },
  computed: {
    currentLang() {
      return this.$route.query.lang || this.$i18n.locale;
    },
    formattedAboutText() {
      if (!this.contactData.translations[this.currentLang]?.about) return "";
      return this.contactData.translations[this.currentLang].about.replace(
        /\n\n/g,
        "<br><br>"
      );
    },
    // Add these computed properties for easier access in template
    translatedAddress() {
      return this.contactData.translations[this.currentLang]?.address || "";
    },
    translatedPhone() {
      return this.contactData.translations[this.currentLang]?.phone || "";
    },
    translatedEmail() {
      return this.contactData.translations[this.currentLang]?.email || "";
    },
  },
  async created() {
    await this.fetchContactData();
  },
  methods: {
    getFullImageUrl(path) {
      return path ? `https://admin.from-malk.com/storage/${path}` : "";
    },
    handleImageError(e) {
      e.target.style.display = "none";
      // Or optionally set placeholder:
      // e.target.src = '/images/placeholder.jpg';
    },
    async fetchContactData() {
      try {
        const response = await api.get(
          `/v1/admin/contact?lang=${this.currentLang}`
        );
        this.contactData = response.data;

        // Set the translations in i18n if needed
        if (response.data.translations) {
          Object.keys(response.data.translations).forEach((lang) => {
            this.$i18n.mergeLocaleMessage(lang, {
              contact: {
                address: response.data.translations[lang].address,
                phone: response.data.translations[lang].phone,
                email: response.data.translations[lang].email,
              },
            });
          });
        }
      } catch (error) {
        console.error("Error fetching contact data:", error);
      }
    },
  },
  watch: {
    "$route.query.lang"(newLang) {
      if (newLang) {
        this.fetchContactData();
      }
    },
  },
  components: {
    MobileSearchBar,
  },
};
</script>

<style scoped>
.prose >>> br {
  content: "";
  display: block;
  margin: 0.75em 0;
}
</style>
